import {useContext, useEffect} from 'react'

import {DidomiSDK} from '@didomi/react'

import cookie from '@s-ui/js/lib/cookie'
import Context from '@s-ui/react-context'
import {useMount} from '@s-ui/react-hooks'
import {useRouter} from '@s-ui/react-router'
import {AuthenticationContext} from '@adv-mt/mt-context-authentication'

import {rejectAll, resetAndCleanCmp} from './actions.js'
import {saveBorosTcfCookie} from './borosTcfCookie.js'
import {setDidomiConfig} from './didomiConfig.js'
import {
  DIDOMI_IDS,
  DISABLE_CONSENTS_QUERY_PARAM,
  SKIP_CMP_COOKIE_NAME,
  SKIP_CMP_FULL_CONSENT_VALUE,
  TCF_VERSION
} from './settings.js'
import {showPurposes} from './showLayers.js'

const {get: getCookie, remove: removeCookie} = cookie

const SELECTOR_OPEN_MODAL = '[data-open-cmp-modal]'

const isClient = typeof window !== 'undefined'

function isRequestConsentsFinished(tcData) {
  return tcData?.eventStatus === 'useractioncomplete'
}

function getConsents(tcData) {
  try {
    return Object.values(tcData?.purpose?.consents)
  } catch (error) {
    return []
  }
}

function SharedCmp() {
  const {isMotos} = useContext(Context)
  const {user} = useContext(AuthenticationContext)
  const {
    location: {pathname, search}
  } = useRouter()

  useEffect(() => {
    if (!isClient) return
    // this configuration can be here or directly in the Didomi console
    // It is put here only to have visibility
    setDidomiConfig()
  }, [])

  const isDidomiSDKDisabled = isClient && window?.location?.search?.includes(DISABLE_CONSENTS_QUERY_PARAM)

  function navigateToLogin() {
    if (typeof window !== 'undefined') {
      const isLoginPage = Boolean(pathname?.match(/login/))

      const targetPathname = isLoginPage ? '/' : pathname
      const currentPath = `${targetPathname}${search}`
      const loginUrl = `/login?cmpAuth=true&targetPage=${currentPath}`

      window.location.href = loginUrl
    }
  }

  function skipCmpForTest() {
    const skipCmpCookie = isClient ? getCookie(SKIP_CMP_COOKIE_NAME) : null
    if (skipCmpCookie === SKIP_CMP_FULL_CONSENT_VALUE) {
      window.Didomi.setUserAgreeToAll()
      removeCookie(SKIP_CMP_COOKIE_NAME)
    }
  }

  const handleReady = () => {
    skipCmpForTest()
    saveBorosTcfCookie() // needed for https://github.com/SUI-Components/sui/tree/master/packages/sui-consents
    if (isMotos) document.getElementById('didomi-host')?.classList.add('didomi-host--motos') // Temporary code to be removed when the new CMP design applies to cochesnet as well
  }

  const handleConsentChanged = () => {
    saveBorosTcfCookie() // needed for https://github.com/SUI-Components/sui/tree/master/packages/sui-consents
  }

  useMount(() => {
    /*
      The reason the event is attached to the document it's because the Footer
      is mounted dynamically via DynamicRendering so we have to do Event Delegation.

      The reason we use click and not a custom Event is to simplify the development
      either in React and Monolith. We would have to do logic in both sides
      in case we want to add a preventDefault() and a customClick() for changing
      the <a> default behaviour.

      Once we migrated the Footer to React, this could be refactored.
    */
    document.addEventListener('click', handleClick, false)
    return () => document.removeEventListener('click', handleClick, false)
  })

  useEffect(() => {
    if (user) return
    if (isMotos) return

    let listenerId = null

    window?.__tcfapi('addEventListener', TCF_VERSION, (tcData, success) => {
      listenerId = tcData.listenerId

      if (success && isRequestConsentsFinished(tcData)) {
        const consents = getConsents(tcData)

        if (consents.length === 0) {
          navigateToLogin()
          return
        }

        const areAllConsentsAccepted = consents.every(Boolean)

        if (!areAllConsentsAccepted) {
          navigateToLogin()
        }
      }
    })

    return () => {
      window?.__tcfapi('removeEventListener', TCF_VERSION, () => null, listenerId)
    }
  }, []) // eslint-disable-line

  function handleClick(event) {
    if (event.target.matches(SELECTOR_OPEN_MODAL)) {
      event.preventDefault()
      showPurposes()
    }
  }

  return (
    !isDidomiSDKDisabled && (
      <div className="mt-SharedCmp">
        <DidomiSDK
          apiKey={DIDOMI_IDS.PUBLIC_API_KEY}
          noticeId={DIDOMI_IDS.NOTICE_ID[isMotos ? 'motosnet' : 'cochesnet']}
          iabVersion={TCF_VERSION}
          gdprAppliesGlobally={true}
          embedTCFStub
          onReady={handleReady}
          onConsentChanged={handleConsentChanged}
        />
      </div>
    )
  )
}

SharedCmp.displayName = 'SharedCmp'

export default SharedCmp

export {rejectAll, resetAndCleanCmp}
